import React, { useState } from 'react';
import { BaseModal } from './BaseModal';
import { UserRole } from '../types';

export interface LoginSignupModalProps {
  isOpen: boolean;
  onClose: () => void;
  isSignUp: boolean;
  onLogin: (email: string, password: string) => Promise<void>;
  onRegister: (userData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    role: UserRole;
  }) => Promise<void>;
  onForgotPasswordClick?: () => void;
}

/**
 * LoginSignupModal - A modal for user authentication (login/signup)
 *
 * Provides forms for both login and signup with validation:
 * - Login: email and password fields
 * - Signup: firstName, lastName, email, password, and confirm password fields
 * - Form validation and error handling
 * - Consistent styling using BaseModal
 */
export const LoginSignupModal: React.FC<LoginSignupModalProps> = ({
  isOpen,
  onClose,
  isSignUp,
  onLogin,
  onRegister,
  onForgotPasswordClick
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role] = useState<UserRole>('student'); // Default to student
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
    setError('');
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      if (isSignUp) {
        if (password !== confirmPassword) {
          setError("Passwords do not match!");
          setIsSubmitting(false);
          return;
        }
        await onRegister({ firstName, lastName, email, password, role });
      } else {
        await onLogin(email, password);
      }
      handleClose();
    } catch (error: any) {
      setError(error.message || "Authentication failed. Please try again.");
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = () => {
    if (isSignUp) {
      return !firstName || !lastName || !email || !password || !confirmPassword || password !== confirmPassword || isSubmitting;
    } else {
      return !email || !password || isSubmitting;
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={handleClose} className="max-w-4xl w-full shadow-large sm:min-w-[500px] md:min-w-[600px]">
      <form onSubmit={handleSubmit}>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-neutral-900">
            {isSignUp ? "Sign Up" : "Log In"}
          </h2>
          <p className="text-neutral-600 mt-2">
            {isSignUp ? "Create your account to get started" : "Welcome back! Please sign in to your account"}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Signup-only fields */}
        {isSignUp && (
          <>
            <div className="mb-4">
              <label htmlFor="firstName" className="block text-sm font-medium text-neutral-700 mb-2">
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder="Your first name"
                required={isSignUp}
                disabled={isSubmitting}
              />
            </div>
            <div className="mb-4">
              <label htmlFor="lastName" className="block text-sm font-medium text-neutral-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder="Your last name"
                required={isSignUp}
                disabled={isSubmitting}
              />
            </div>
          </>
        )}

        {/* Email field */}
        <div className="mb-4">
          <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">
            Email
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
            placeholder="your@email.com"
            required
            disabled={isSubmitting}
          />
        </div>

        {/* Password field */}
        <div className="mb-4">
          <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-2">
            Password
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-colors"
            placeholder="Enter your password"
            required
            disabled={isSubmitting}
          />
        </div>

        {/* Forgot password link - only show in login mode */}
        {!isSignUp && onForgotPasswordClick && (
          <div className="text-right mb-4">
            <button
              type="button"
              onClick={() => {
                handleClose();
                onForgotPasswordClick();
              }}
              className="text-sm text-blue-600 hover:text-blue-500 font-medium"
            >
              Forgot your password?
            </button>
          </div>
        )}

        {/* Confirm password field (signup only) */}
        {isSignUp && (
          <div className="mb-4">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700 mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg shadow-sm transition-colors ${
                password && confirmPassword && password !== confirmPassword
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-neutral-300 focus:ring-primary-500 focus:border-primary-500'
              }`}
              placeholder="Confirm your password"
              required={isSignUp}
              disabled={isSubmitting}
            />
            {password && confirmPassword && password !== confirmPassword && (
              <p className="text-red-500 text-xs mt-1">Passwords do not match.</p>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors font-medium"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitDisabled()}
            className={`flex-1 px-4 py-2 rounded-lg text-white font-medium transition-colors ${
              isSubmitDisabled()
                ? 'bg-neutral-300 cursor-not-allowed'
                : 'bg-primary-600 hover:bg-primary-700'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="loading-spinner mr-2"></div>
                {isSignUp ? 'Creating Account...' : 'Signing In...'}
              </div>
            ) : (
              isSignUp ? 'Sign Up' : 'Log In'
            )}
          </button>
        </div>
      </form>
    </BaseModal>
  );
};